import { useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { clean, format, validate } from "rut.js";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";

interface AddMaquilaClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientAdded: (client: { id: string; name: string }) => void;
}

interface MaquilaClientFormValues {
  name: string;
  tax_id: string;
  phone_number: string;
  email: string;
  contact_person: string;
}

export const AddMaquilaClientDialog = ({
  open,
  onOpenChange,
  onClientAdded,
}: AddMaquilaClientDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id: userId } = useProfileStore();

  const form = useForm<MaquilaClientFormValues>({
    defaultValues: {
      name: "",
      tax_id: "",
      phone_number: "",
      email: "",
      contact_person: "",
    },
    mode: "onChange",
  });

  const handleRutChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    let value = e.target.value;
    value = clean(value);
    value = value.replace(/[^0-9kK]/g, "");

    if (value.length > 0) {
      const formattedValue = format(value).replace(/\./g, "");
      onChange(formattedValue);
    } else {
      onChange(value);
    }
  };

  const handleSubmit = async (values: MaquilaClientFormValues) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar el usuario actual.",
        variant: "destructive",
      });
      return;
    }

    // Validate RUT format
    if (!values.tax_id || !validate(values.tax_id)) {
      form.setError("tax_id", {
        type: "manual",
        message: "RUT invalido. Ingrese un RUT valido.",
      });
      return;
    }

    const standardizedRut = format(values.tax_id).replace(/\./g, "");
    const standardizedName = values.name.toUpperCase().trim();

    setIsSubmitting(true);

    try {
      // Check if RUT already exists
      const { data: existingClients, error: rutError } = await supabase
        .from("clients")
        .select("id")
        .eq("tax_id", standardizedRut)
        .limit(1);

      if (rutError) throw rutError;

      if (existingClients && existingClients.length > 0) {
        form.setError("tax_id", {
          type: "manual",
          message: "Ya existe un cliente con este RUT.",
        });
        setIsSubmitting(false);
        return;
      }

      // Insert new maquila client
      const { data, error } = await supabase
        .from("clients")
        .insert({
          name: standardizedName,
          tax_id: standardizedRut,
          phone_number: values.phone_number || null,
          email: values.email || null,
          contact_person: values.contact_person || null,
          created_by: userId,
          assigned_seller: userId,
          is_maquila_client: true,
          sales_condition_id: "c7b68ad1-3869-4f0b-b35b-e07a263fd43e", // TRANSFERENCIA ANTICIPADA
        })
        .select("id, name")
        .single();

      if (error) throw error;

      toast({
        title: "Cliente creado",
        description: `El cliente "${data.name}" ha sido creado exitosamente.`,
      });

      onClientAdded({ id: data.id, name: data.name ?? standardizedName });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating maquila client:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el cliente. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  const isValid = form.watch("name") && form.watch("tax_id");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo Cliente Maquila</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "El nombre es requerido" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre / Razon Social *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Nombre del cliente"
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tax_id"
              rules={{ required: "El RUT es requerido" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RUT *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="12345678-9"
                      onChange={(e) => handleRutChange(e, field.onChange)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact_person"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Persona de Contacto</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nombre del contacto" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefono</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="+56 9 1234 5678" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="correo@ejemplo.com"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!isValid || isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Crear Cliente
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
